import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { AvatarBadge } from "@/components/Avatars";
import {
  ArrowLeft, Users, Target, TrendingUp, Zap, Award, Lightbulb, Unlock, FileText, Eye,
  PenLine, X, Loader2, CheckCircle2, Send,
} from "lucide-react";


function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: `${tint}1A` }}><Icon size={22} color={tint} /></div>
        <div><div className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">{label}</div><div className="font-heading text-2xl font-extrabold text-[#1F2937]">{value}</div></div>
      </div>
    </div>
  );
}

const TABS = ["Overview", "Curriculum", "Writing Samples"];

function AssignCursiveModal({ open, onClose, studentId, studentName, onAssigned }) {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [levelId, setLevelId] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [classmates, setClassmates] = useState([]);
  const [extraStudents, setExtraStudents] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.getCurriculum(studentId).then((d) => {
      const ls = d.levels || [];
      setLevels(ls);
      const first = ls.find((l) => !l.coming_soon) || ls[0];
      if (first) setLevelId(first.id);
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    // Load the caller's other students so the assignment can fan out to a class.
    api.listStudents().then((all) => {
      setClassmates((all || []).filter((x) => x.id !== studentId && !x.is_deleted));
    }).catch(() => setClassmates([]));
    setSelected(new Set());
    setExtraStudents(new Set());
    setTitle("");
    setDueDate("");
  }, [open, studentId]);

  const currentLevel = useMemo(() => levels.find((l) => l.id === levelId), [levels, levelId]);
  const allActivities = useMemo(() => {
    if (!currentLevel) return [];
    return (currentLevel.groups || []).flatMap((g) => (g.activities || []).map((a) => ({ ...a, groupName: g.name })));
  }, [currentLevel]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setSelected(new Set(allActivities.map((a) => a.id)));
  const clearAll = () => setSelected(new Set());

  const toggleStudent = (id) => setExtraStudents((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllStudents = () => setExtraStudents(new Set(classmates.map((s) => s.id)));
  const clearAllStudents = () => setExtraStudents(new Set());

  const submit = async () => {
    if (selected.size === 0) { toast.error("Pick at least one activity"); return; }
    setBusy(true);
    try {
      const activity_ids = [...selected];
      const student_ids = [studentId, ...extraStudents];
      const totalStudents = student_ids.length;
      const finalTitle = title.trim() || `Cursive: ${currentLevel?.title || "practice"} (${activity_ids.length} activity${activity_ids.length === 1 ? "" : "ies"})`;
      await api.createAssignment({
        title: finalTitle,
        activity_type: "activity",
        activity_ids,
        student_ids,
        due_date: dueDate || undefined,
      });
      // Auto-unlock the target level for every student so they can play immediately.
      await Promise.all(student_ids.map((sid) => api.unlock({ student_id: sid, level_id: levelId }).catch(() => null)));
      const who = totalStudents === 1 ? (studentName || "student") : `${totalStudents} students`;
      toast.success(`Assigned ${activity_ids.length} activit${activity_ids.length === 1 ? "y" : "ies"} to ${who}`);
      onAssigned && onAssigned();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#1F2937]/60 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="assign-cursive-modal">
          <motion.div initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
            className="w-full max-w-2xl rounded-[28px] bg-white p-6 shadow-2xl border border-[#E5DECF] overflow-y-auto max-h-[90vh]">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0D9488]/10 text-[#0D9488]"><PenLine size={22} /></div>
                <div>
                  <h3 className="font-heading text-xl font-extrabold text-[#1F2937]">Assign cursive writing</h3>
                  <p className="font-body text-xs text-[#6B7280]">Pick any level and activities. The target level unlocks automatically for the student.</p>
                </div>
              </div>
              <button onClick={onClose} data-testid="assign-cursive-close" className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
            </div>

            {loading ? (
              <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>
            ) : (
              <div className="mt-5 space-y-4">
                <label className="block">
                  <span className="text-sm font-bold text-[#1F2937]">Level</span>
                  <select value={levelId} onChange={(e) => { setLevelId(e.target.value); setSelected(new Set()); }} data-testid="assign-cursive-level"
                    className="mt-1 w-full rounded-2xl border-2 border-[#E5DECF] bg-white px-4 py-3 font-body text-[#1F2937] outline-none focus:border-[#0D9488]">
                    {levels.map((l) => (
                      <option key={l.id} value={l.id}>
                        Level {l.level_number}: {l.title}{l.coming_soon ? " (coming soon)" : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-[#1F2937]">Activities <span className="text-[#94A3B8] font-normal">({selected.size} selected)</span></span>
                    <div className="flex gap-2 text-xs">
                      <button onClick={selectAll} data-testid="assign-select-all" className="rounded-full border border-[#E5DECF] px-3 py-1 font-bold text-[#1F2937] hover:border-[#0D9488]">Select all</button>
                      <button onClick={clearAll} data-testid="assign-clear-all" className="rounded-full border border-[#E5DECF] px-3 py-1 font-bold text-[#6B7280] hover:border-[#1F2937]">Clear</button>
                    </div>
                  </div>
                  <div className="mt-2 max-h-72 space-y-3 overflow-y-auto rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-3">
                    {(currentLevel?.groups || []).map((g) => (
                      <div key={g.id}>
                        <div className="text-xs uppercase font-bold text-[#94A3B8] mb-1">{g.name}</div>
                        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                          {(g.activities || []).map((a) => (
                            <label key={a.id} className={`flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-sm cursor-pointer transition-colors ${selected.has(a.id) ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0B7F74]" : "border-[#E5DECF] bg-white hover:border-[#0D9488]/40"}`}
                              data-testid={`assign-activity-${a.id}`}>
                              <input type="checkbox" className="h-3.5 w-3.5 accent-[#0D9488]" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
                              <span className="truncate font-body">{a.title || a.content || a.id}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    {(!currentLevel || allActivities.length === 0) && (
                      <div className="p-6 text-center text-sm text-[#6B7280]">No activities in this level yet.</div>
                    )}
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-bold text-[#1F2937]">Title (optional)</span>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="assign-cursive-title"
                      placeholder={`Cursive: ${currentLevel?.title || "practice"}`}
                      className="mt-1 w-full rounded-2xl border-2 border-[#E5DECF] bg-white px-4 py-3 font-body text-[#1F2937] outline-none focus:border-[#0D9488]" />
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-[#1F2937]">Due date (optional)</span>
                    <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="assign-cursive-due"
                      className="mt-1 w-full rounded-2xl border-2 border-[#E5DECF] bg-white px-4 py-3 font-body text-[#1F2937] outline-none focus:border-[#0D9488]" />
                  </label>
                </div>

                {classmates.length > 0 && (
                  <div data-testid="assign-classmates">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-[#1F2937]">Also assign to <span className="text-[#94A3B8] font-normal">({extraStudents.size} of {classmates.length} classmates)</span></span>
                      <div className="flex gap-2 text-xs">
                        <button onClick={selectAllStudents} data-testid="assign-classmates-all" className="rounded-full border border-[#E5DECF] px-3 py-1 font-bold text-[#1F2937] hover:border-[#0D9488]">Select all classmates</button>
                        <button onClick={clearAllStudents} data-testid="assign-classmates-clear" className="rounded-full border border-[#E5DECF] px-3 py-1 font-bold text-[#6B7280] hover:border-[#1F2937]">Clear</button>
                      </div>
                    </div>
                    <div className="mt-2 max-h-48 overflow-y-auto rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-2">
                      <div className="mb-1.5 px-1.5 text-xs italic text-[#94A3B8]">{studentName || "This student"} is always included.</div>
                      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {classmates.map((cm) => (
                          <label key={cm.id} className={`flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-sm cursor-pointer transition-colors ${extraStudents.has(cm.id) ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0B7F74]" : "border-[#E5DECF] bg-white hover:border-[#0D9488]/40"}`}
                            data-testid={`assign-classmate-${cm.id}`}>
                            <input type="checkbox" className="h-3.5 w-3.5 accent-[#0D9488]" checked={extraStudents.has(cm.id)} onChange={() => toggleStudent(cm.id)} />
                            <span className="truncate font-body">{cm.name}</span>
                            <span className="ml-auto shrink-0 text-xs text-[#94A3B8]">{cm.grade || ""}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button onClick={onClose} data-testid="assign-cursive-cancel" className="rounded-full border-2 border-[#E5DECF] px-5 py-2.5 font-bold text-[#6B7280] hover:border-[#1F2937]">Cancel</button>
                  <button onClick={submit} disabled={busy || selected.size === 0} data-testid="assign-cursive-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-[#0D9488] px-5 py-2.5 font-heading font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Assign & unlock{extraStudents.size > 0 ? ` (${extraStudents.size + 1})` : ""}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function StudentProgressView() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [samples, setSamples] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [error, setError] = useState("");
  const [showAssign, setShowAssign] = useState(false);

  const load = useCallback(() => {
    api.report(studentId).then(setReport).catch((e) => { const m = formatApiError(e); setError(m); });
    api.listSamples(studentId).then(setSamples).catch(() => {});
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const assignPractice = async (rec) => {
    try {
      await api.createAssignment({ title: `Practice ${rec.title}`, activity_type: rec.type, activity_ids: [rec.activity_id], student_ids: [studentId] });
      toast.success(`Assigned: ${rec.title}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unlockLevel = async (levelId) => {
    try { await api.unlock({ student_id: studentId, level_id: levelId }); toast.success("Level unlocked"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const unlockThrough = async (levelNumber) => {
    const ids = Array.from({ length: levelNumber }, (_, i) => `level_${i + 1}`);
    try {
      await api.unlock({ student_id: studentId, level_ids: ids });
      toast.success(`Unlocked Levels 1 – ${levelNumber}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const review = async (sample, rating) => {
    try { await api.reviewSample(sample.id, { rating, comment: "" }); toast.success("Review saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (error) return <div className="p-16 text-center"><p className="font-heading text-xl font-bold text-[#E05A47]">{error}</p><button onClick={() => navigate(-1)} className="mt-4 rounded-2xl bg-[#0D9488] px-5 py-3 font-bold text-white">Back</button></div>;
  if (!report) return <div className="p-16 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading…</div>;
  const s = report.student;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <button onClick={() => navigate(-1)} data-testid="back-to-students" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#0D9488]"><ArrowLeft size={16} /> Back</button>
      <div className="mt-4 flex items-center gap-4">
        {s && <AvatarBadge avatarKey={s.avatar} size={56} />}
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#0284C7]">Student Progress</div>
          <h1 className="font-heading text-3xl font-extrabold text-[#1F2937]">{s?.name}</h1>
          <p className="font-body text-[#6B7280]">{s?.grade} · @{s?.username} · {report.points} pts</p>
        </div>
        <button onClick={() => window.print()} className="ml-auto hidden sm:inline-flex items-center gap-2 rounded-2xl border-2 border-[#E5DECF] px-4 py-2.5 font-bold text-[#1F2937] hover:border-[#0D9488]"><FileText size={16} /> Print Report</button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={() => navigate(`/students/${s?.id}/progress`)} data-testid="view-all-subjects"
          className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-4 py-2.5 font-heading font-bold text-white hover:bg-[#0B7F74]">
          Track lesson progress · Reading, Math, Science, Language…
        </button>
        <button onClick={() => setShowAssign(true)} data-testid="open-assign-cursive"
          className="inline-flex items-center gap-2 rounded-2xl border-2 border-[#0D9488] bg-white px-4 py-2.5 font-heading font-bold text-[#0D9488] hover:bg-[#0D9488]/10">
          <PenLine size={16} /> Assign cursive writing
        </button>
        <button onClick={() => navigate(`/students/${s?.id}/certificates`)} data-testid="open-certificates"
          className="inline-flex items-center gap-2 rounded-2xl border-2 border-[#7A3FF2] bg-white px-4 py-2.5 font-heading font-bold text-[#7A3FF2] hover:bg-[#7A3FF2]/10">
          <Award size={16} /> Certificates
        </button>
      </div>

      <div className="mt-6 flex gap-2 border-b border-[#E5DECF]">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
            className={`border-b-2 px-4 py-3 text-sm font-bold transition-colors ${tab === t ? "border-[#0D9488] text-[#0D9488]" : "border-transparent text-[#6B7280] hover:text-[#1F2937]"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="mt-6 space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Target} label="Overall Progress" value={`${report.overall_percentage}%`} tint="#0D9488" />
            <Stat icon={Users} label="Skills Mastered" value={`${report.skills_mastered}/${report.skills_total}`} tint="#10B981" />
            <Stat icon={TrendingUp} label="Avg Accuracy" value={`${report.average_accuracy}%`} tint="#F59E0B" />
            <Stat icon={Zap} label="Activities Done" value={report.activities_completed} tint="#0284C7" />
          </div>

          {report.badges?.length > 0 && (
            <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
              <div className="flex items-center gap-2 text-[#B45309]"><Award size={18} /><div className="font-heading text-lg font-bold">Badges</div></div>
              <div className="mt-3 flex flex-wrap gap-2">{report.badges.map((b) => <span key={b.id} className="rounded-full bg-[#F59E0B]/15 px-3 py-1 text-sm font-bold text-[#B45309]">{b.name}</span>)}</div>
            </div>
          )}

          {report.recommendations?.length > 0 && (
            <div className="rounded-3xl border-2 border-[#F59E0B]/40 bg-[#F59E0B]/5 p-5">
              <div className="flex items-center gap-2 text-[#B45309]"><Lightbulb size={18} /><div className="font-heading text-lg font-bold">Skills Needing Practice</div></div>
              <div className="mt-3 flex flex-wrap gap-3">
                {report.recommendations.map((r) => (
                  <button key={r.activity_id} onClick={() => assignPractice(r)} data-testid={`assign-${r.activity_id}`}
                    className="inline-flex items-center gap-2 rounded-2xl bg-white border border-[#E5DECF] px-4 py-2.5 font-bold text-[#1F2937] hover:border-[#F59E0B]">
                    Assign {r.title} ({r.best_score}%)
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "Curriculum" && (
        <div className="mt-6 space-y-3" data-testid="curriculum-tab">
          <div className="rounded-2xl border border-dashed border-[#0D9488]/40 bg-[#0D9488]/5 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs font-body text-[#0B7F74] flex-1 min-w-[220px]">
                <strong>Skip the basics:</strong> unlock a range of levels for older learners who don't need pre-writing or letter drills.
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="font-bold text-[#6B7280]">Unlock through</span>
                {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <button key={n} onClick={() => unlockThrough(n)} data-testid={`unlock-through-${n}`}
                    className="rounded-full border-2 border-[#0D9488]/40 bg-white px-2.5 py-1 font-heading font-bold text-[#0D9488] hover:border-[#0D9488] hover:bg-[#0D9488]/10">
                    L{n}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {report.levels.map((l) => (
            <div key={l.level_number} className="flex items-center justify-between rounded-2xl border border-[#E5DECF] bg-white p-4">
              <div>
                <div className="font-heading font-bold text-[#1F2937]">Level {l.level_number}: {l.title}</div>
                <div className="mt-1 h-2 w-48 max-w-[50vw] overflow-hidden rounded-full bg-[#F2ECE1]"><div className="h-full bg-[#0D9488]" style={{ width: `${l.completion_percentage}%` }} /></div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-[#6B7280] capitalize">{l.status.replace("_", " ")}</span>
                {l.status === "locked" ? (
                  <button onClick={() => unlockLevel(`level_${l.level_number}`)} data-testid={`unlock-level-${l.level_number}`}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#0D9488] px-3 py-2 text-sm font-bold text-white hover:bg-[#0B7F74]"><Unlock size={14} /> Unlock</button>
                ) : (
                  <button onClick={() => unlockLevel(`level_${l.level_number}`)} data-testid={`reunlock-level-${l.level_number}`}
                    title="Grant explicit access (already available to the student)"
                    className="inline-flex items-center gap-1.5 rounded-xl border-2 border-[#E5DECF] px-3 py-2 text-sm font-bold text-[#6B7280] hover:border-[#0D9488] hover:text-[#0D9488]"><Unlock size={14} /> Grant access</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Writing Samples" && (
        <div className="mt-6 space-y-3" data-testid="samples-tab">
          {samples.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[#E5DECF] bg-white p-10 text-center text-[#94A3B8]">No saved writing samples yet.</div>
          ) : samples.map((sm) => (
            <div key={sm.id} className="rounded-2xl border border-[#E5DECF] bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-[#1F2937] flex items-center gap-2"><Eye size={16} /> {sm.prompt || "Free write"}</div>
                  <div className="text-xs text-[#6B7280]">{new Date(sm.created_at).toLocaleString()} · {sm.stroke_data?.length || 0} strokes</div>
                  {sm.review && <div className="mt-1 text-sm font-semibold text-[#047857]">Reviewed: {sm.review.rating}</div>}
                </div>
                <div className="flex gap-2">
                  {["Excellent", "Good Progress", "Needs More Practice"].map((r) => (
                    <button key={r} onClick={() => review(sm, r)} data-testid={`review-${sm.id}-${r.replace(/\s+/g, "-").toLowerCase()}`} aria-label={`Mark as ${r}`} className="rounded-xl bg-[#F2ECE1] px-3 py-1.5 text-xs font-bold text-[#1F2937] hover:bg-[#E5DECF]">{r}</button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AssignCursiveModal
        open={showAssign}
        onClose={() => setShowAssign(false)}
        studentId={studentId}
        studentName={s?.name}
        onAssigned={load}
      />
    </div>
  );
}
